package br.com.duoma.duomaproducao.ui.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.sqrt

data class NodeRect(val x: Float, val y: Float, val w: Float, val h: Float) {
    val centerX get() = x + w / 2f
    val centerY get() = y + h / 2f
    val right    get() = x + w
    val bottom   get() = y + h
}

@Composable
fun FlowchartArrows(
    rects: Map<String, NodeRect>,
    modifier: Modifier = Modifier,
    color: Color = Color(0xFF444444),
    strokeWidthDp: Float = 1.5f,
    bottomArrowMargin : Dp = 24.dp
) {
    val density = LocalDensity.current
    val strokePx = with(density) { strokeWidthDp.dp.toPx() }
    val arrowSize = with(density) { 8.dp.toPx() }

    Canvas(modifier = modifier.fillMaxSize()) {
        if (rects.size < 8) return@Canvas  // not all nodes measured yet

        val c = rects["corte"]!!
        val co = rects["costura"]!!
        val ma = rects["marcenaria"]!!
        val la = rects["laminacao"]!!
        val mo = rects["montagem"]!!
        val pr = rects["preparacao"]!!
        val es = rects["espumacao"]!!
        val ta = rects["tapecaria"]!!

        // 1. Corte → Costura (straight horizontal)
        drawArrow(
            from = Offset(c.right, c.centerY),
            to   = Offset(co.x, co.centerY),
            color = color, strokePx = strokePx, arrowSize = arrowSize
        )

        // 2. Costura → Tapeçaria (straight down)
        drawArrow(
            from = Offset(co.centerX, co.bottom),
            to   = Offset(ta.centerX, ta.y),
            color = color, strokePx = strokePx, arrowSize = arrowSize
        )

        // 3. Marcenaria → Montagem (straight horizontal)
        drawArrow(
            from = Offset(ma.right, ma.centerY),
            to   = Offset(mo.x, mo.centerY),
            color = color, strokePx = strokePx, arrowSize = arrowSize
        )

        // 3b. Laminação → Espumação (U-shape: down from la.bottom → horizontal → up into es.bottom)
        val uBottomY = maxOf(la.bottom, es.bottom) + with(density) { bottomArrowMargin.toPx() }
        // left leg: down from Laminação bottom
        drawLine(color, Offset(la.centerX, la.bottom), Offset(la.centerX, uBottomY), strokePx)
        // bottom: horizontal across
        drawLine(color, Offset(la.centerX, uBottomY), Offset(es.centerX, uBottomY), strokePx)
        // right leg + arrowhead: up into Espumação bottom
        drawArrow(
            from = Offset(es.centerX, uBottomY),
            to   = Offset(es.centerX, es.bottom),
            color = color, strokePx = strokePx, arrowSize = arrowSize
        )

        // 4. Montagem → Preparação (straight down)
        drawArrow(
            from = Offset(mo.centerX, mo.bottom),
            to   = Offset(pr.centerX, pr.y),
            color = color, strokePx = strokePx, arrowSize = arrowSize
        )

        // 5. Preparação → Espumação (straight right)
        drawArrow(
            from = Offset(pr.right, pr.centerY),
            to   = Offset(es.x, es.centerY),
            color = color, strokePx = strokePx, arrowSize = arrowSize
        )

        // 6. Espumação → Tapeçaria (straight up)
        drawArrow(
            from = Offset(es.centerX, es.y),
            to   = Offset(ta.centerX, ta.bottom),
            color = color, strokePx = strokePx, arrowSize = arrowSize
        )
    }
}


// Mobile layout arrow map (2×4 grid, flow top→bottom):
//   col1 (left)   col2 (right)
//   [corte]       [costura]       row 1
//   [marcenaria]  [tapecaria]     row 2
//   [laminacao]   [montagem]      row 3
//   [preparacao]  [espumacao]     row 4
//
// Connections preserved from desktop:
//   corte → costura       (→ horizontal, row 1)
//   costura → tapecaria   (↓ vertical,  col 2)
//   marcenaria → montagem (Z through row2-row3 gap)
//   laminacao → espumacao (Z through row3-row4 gap, upper lane)
//   montagem → preparacao (Z through row3-row4 gap, lower lane)
//   preparacao → espumacao(→ horizontal, row 4)
//   espumacao → tapecaria (↑ U-shape on right side)

@Composable
fun FlowchartArrowsMobile(
    rects: Map<String, NodeRect>,
    modifier: Modifier = Modifier,
    color: Color = Color(0xFF444444),
    strokeWidthDp: Float = 1.5f,
    sideMarginDp: Float = 10f
) {
    val density = LocalDensity.current
    val strokePx  = with(density) { strokeWidthDp.dp.toPx() }
    val arrowSize = with(density) { 8.dp.toPx() }
    val sidePx    = with(density) { sideMarginDp.dp.toPx() }
    val lanePx    = with(density) { 6.dp.toPx() }

    Canvas(modifier = modifier.fillMaxSize()) {
        if (rects.size < 8) return@Canvas

        val c  = rects["corte"]!!
        val co = rects["costura"]!!
        val ma = rects["marcenaria"]!!
        val ta = rects["tapecaria"]!!
        val la = rects["laminacao"]!!
        val mo = rects["montagem"]!!
        val pr = rects["preparacao"]!!
        val es = rects["espumacao"]!!

        // 1. Corte → Costura (horizontal right)
        drawArrow(Offset(c.right, c.centerY), Offset(co.x, co.centerY), color, strokePx, arrowSize)

        // 2. Costura → Tapeçaria (straight down)
        drawArrow(Offset(co.centerX, co.bottom), Offset(ta.centerX, ta.y), color, strokePx, arrowSize)

        // 3. Marcenaria → Montagem (Z: ↓ col1 → → gap → ↓ into mo.top)
        val gapY23 = (ma.bottom + la.y) / 2f
        drawLine(color, Offset(ma.centerX, ma.bottom), Offset(ma.centerX, gapY23), strokePx)
        drawLine(color, Offset(ma.centerX, gapY23), Offset(mo.centerX, gapY23), strokePx)
        drawArrow(Offset(mo.centerX, gapY23), Offset(mo.centerX, mo.y), color, strokePx, arrowSize)

        // 4. Laminação → Espumação (Z: ↓ col1 → → upper lane → ↓ into es.top)
        val gapY34base = (la.bottom + pr.y) / 2f
        val upperLane  = gapY34base - lanePx
        drawLine(color, Offset(la.centerX, la.bottom), Offset(la.centerX, upperLane), strokePx)
        drawLine(color, Offset(la.centerX, upperLane), Offset(es.centerX, upperLane), strokePx)
        drawArrow(Offset(es.centerX, upperLane), Offset(es.centerX, es.y), color, strokePx, arrowSize)

        // 5. Montagem → Preparação (Z: ← col2 → lower lane → ↓ into pr.top)
        val lowerLane = gapY34base + lanePx
        drawLine(color, Offset(mo.centerX, mo.bottom), Offset(mo.centerX, lowerLane), strokePx)
        drawLine(color, Offset(mo.centerX, lowerLane), Offset(pr.centerX, lowerLane), strokePx)
        drawArrow(Offset(pr.centerX, lowerLane), Offset(pr.centerX, pr.y), color, strokePx, arrowSize)

        // 6. Preparação → Espumação (horizontal right)
        drawArrow(Offset(pr.right, pr.centerY), Offset(es.x, es.centerY), color, strokePx, arrowSize)

        // 7. Espumação → Tapeçaria (U-shape up the right side)
        val rightX = maxOf(es.right, ta.right) + sidePx
        drawLine(color, Offset(es.right, es.centerY), Offset(rightX, es.centerY), strokePx)
        drawLine(color, Offset(rightX, es.centerY), Offset(rightX, ta.centerY), strokePx)
        drawArrow(Offset(rightX, ta.centerY), Offset(ta.right, ta.centerY), color, strokePx, arrowSize)
    }
}


// ─────────────────────────────────────────────
// DrawScope helpers
// ─────────────────────────────────────────────

private fun DrawScope.drawLine(color: Color, from: Offset, to: Offset, strokePx: Float) {
    drawLine(color = color, start = from, end = to, strokeWidth = strokePx)
}

private fun DrawScope.drawArrow(
    from: Offset,
    to: Offset,
    color: Color,
    strokePx: Float,
    arrowSize: Float,
) {
    drawLine(color = color, start = from, end = to, strokeWidth = strokePx)

    // Arrowhead: two lines forming a V at [to], pointing in the direction from→to
    val dx = to.x - from.x
    val dy = to.y - from.y
    val len = sqrt(dx * dx + dy * dy)
    if (len == 0f) return

    val ux = dx / len  // unit vector
    val uy = dy / len

    val angle = PI / 180.0 * 25.0
    val cos = cos(angle).toFloat()
    val sin = sin(angle).toFloat()

    // Rotate unit vector ±angle to get the two barb directions
    val lx = -(ux * cos - uy * sin) * arrowSize
    val ly = -(ux * sin + uy * cos) * arrowSize
    val rx = -(ux * cos + uy * sin) * arrowSize
    val ry = -(-ux * sin + uy * cos) * arrowSize

    drawLine(color = color, start = to, end = Offset(to.x + lx, to.y + ly), strokeWidth = strokePx)
    drawLine(color = color, start = to, end = Offset(to.x + rx, to.y + ry), strokeWidth = strokePx)
}
